import { EnumPrecioUnitario, EnumTipoPeaje } from '../../../../oData'
import { EnumPresionTension } from '../../../../oData/enums/enumPresionTension'
import { EnumPriceType } from '../../../../oData/enums/enumPriceType'
import { EnumRegionImpuestos } from '../../../../oData/enums/enumRegionImpuestos'
import {
  EnumCommodity,
  EnumMarketType,
  EnumPriceCalculationType,
  EnumRedondeo
} from '../../../enums'
import { EnumPriceComponentReference } from '../../../enums/enumPriceComponentReference'
import { ContractSummaryModel } from '../../contracts/model/contracts'
import { FlexibilitySummaryModel } from '../../flexibilities/model/flexibilities'
import { LeadModel } from '../../leads/model/leads'
import { PriceCoefficientAPI } from '../../priceCoefficients/model/PriceCoefficient'
import { PricePeriodValueModel, PricePeriodValueRequestModel } from '../../pricing/model/values'
import { QuotesModel } from '../../quotes/model/quotes'

export interface PriceComponentAPI {
  id: number
  typeCode: string
  typeDescription: string
  idPrice: number
  idPriceComponentType: number
  currentPriceValue: number

  priceType?: EnumPriceType
  priceReference?: EnumPriceComponentReference
  commodity?: EnumCommodity
  idContract?: number
  idFlexibility?: number
  toll?: EnumTipoPeaje
  idLead?: number
  regionImpuestos?: string
  currentPriceValueString?: string[]
  priceValues?: PricePeriodValueModel[]
}

export interface PriceComponentTable extends Omit<PriceComponentAPI, 'currentPriceValue'> {
  isParameter: boolean

  currentPriceValue?: string
  commodity?: number
  defaultValue?: string
  idContract?: number
  idFlexibilidad?: number
  toll?: EnumTipoPeaje
}

export interface PriceComponentTypeModel {
  id?: number
  code: string
  description?: string
  idPriceDummy?: number
  priceDummy?: PriceSummaryModel
  referenceType: EnumPriceComponentReference
  priceType: EnumPriceType
  commodity: EnumCommodity
  company: number
  country: number
}

export interface CreatePriceComponentRequest {
  priceComponent: Pick<PriceComponentAPI, 'idPriceComponentType' | 'idContract' | 'idFlexibility'>
  price: NewPriceRequest
  priceCoefficients?: Omit<PriceCoefficientAPI, 'id' | 'idPriceVersion'>[]
}

export interface UpdatePriceComponentValueRequest {
  id: number
  newPriceValue: number
}

interface NewPriceRequest {
  description?: string
  code: string
  company: number
  commodity: number
  priceType: EnumPriceType
  priceCalculationType?: EnumPriceCalculationType | null
  marketType?: EnumMarketType
  startDate: string
  endDate?: string
  expression?: string | null
  priceUnit: EnumPrecioUnitario
  round: EnumRedondeo
  value?: number | null
}

export interface ArgentiaPricingTableModel {
  idPrice?: number
  idPriceComponent?: number
  idPriceComponentType?: number
  idDummyPrice?: number
  priceComponentTypeCode?: string
  priceComponentTypeDescription?: string
  priceCode?: string
  priceDescription?: string
  priceType?: EnumPriceType
  priceComponentTypeReference?: EnumPriceComponentReference
  commodity?: EnumCommodity
  companyId?: number
  version: ArgentiaPricingTableVersionModel
  relatedPriceComponentCount?: number
  marketType?: EnumMarketType
}

export interface ArgentiaPricingTableVersionModel {
  idVersion?: number
  unitPrice?: EnumPrecioUnitario
  startDate?: Date
  endDate?: Date
  rounding?: EnumRedondeo
  expression?: string
  priceCalculationType?: EnumPriceCalculationType
}

export interface PriceSummaryModel {
  id?: number
  priceType: EnumPriceType
  unitPrice: EnumPrecioUnitario
  rounding?: EnumRedondeo
  priceCalculationType?: EnumPriceCalculationType
  expression: string
  values?: PricePeriodValueModel[]
  coefficients?: PriceCoefficientAPI[]
  commodity?: EnumCommodity
}
export interface PriceSummaryRequestModel extends Omit<
  PriceSummaryModel,
  'values' | 'coefficients'
> {
  values?: PricePeriodValueRequestModel[]
  coefficients?: Omit<PriceCoefficientAPI, 'id' | 'idPriceVersion'>[]
}

export interface PriceComponentModelV2 {
  id?: number
  price: PriceSummaryModel
  priceComponentType: PriceComponentTypeModel
  contract: ContractSummaryModel
  flexitibility: FlexibilitySummaryModel
  toll?: EnumTipoPeaje
  quote: QuotesModel
  lead: LeadModel
  presionTension: EnumPresionTension
  regionImpuestos?: { id: number }
  isDefaultValue?: boolean
}

export interface PriceComponentV2RequestModel {
  id?: number
  price: PriceSummaryRequestModel
  priceComponentType: {
    id: number
  }
  toll?: EnumTipoPeaje
  presionTension?: EnumPresionTension
  regionImpuestos?: EnumRegionImpuestos
  lead?: Partial<LeadModel>
  contract?: Partial<ContractSummaryModel>
  flexibility?: Partial<FlexibilitySummaryModel>
}

// This section helps find out which property/ies must be used to handle each Entity.
// invalidEntity is used to handle if property is not within model.
export const priceComponentReferenceTypeEntityMap: Record<EnumPriceComponentReference, string> = {
  [EnumPriceComponentReference.Contrato]: 'contract',
  [EnumPriceComponentReference.Flexibilidad]: 'flexibility',
  [EnumPriceComponentReference.Peaje]: 'toll',
  [EnumPriceComponentReference.Oferta]: 'quote',
  [EnumPriceComponentReference.Lead]: 'lead',
  [EnumPriceComponentReference.PresionTension]: 'presionTension',
  [EnumPriceComponentReference.RegionImpuestos]: 'regionImpuestos',
  [EnumPriceComponentReference.General]: 'invalidEntity'
}

// relatedEntity helper function
/**
 * This function helps to find out which property/ies must be used to handle each Entity.
 * @param domain  EnumDominioTarea
 * @returns  string | undefined . String property path. 'invalidEntity' in case the entity is not suitable for TasksPage or undefined if the domain is not found.
 */
export function getPriceComponentModelV2RelatedEntityPropertyPath(
  domain: EnumPriceComponentReference | undefined
): string | undefined {
  return domain && priceComponentReferenceTypeEntityMap[domain]
}

export type PriceComponentInstancesDataTableModel = PriceComponentModelV2 & {
  referenceTypeEntityPath: string | null
  referenceTypeEntityCode: string | null
  referenceTypeEntityDescription: string | null
  isDefaultValue: boolean
}
